import getJobName from "./getJobName";

// Email configuration object - single source of truth for all email content
const EMAIL_CONFIGS = {
  // Product job storyboard emails
  PRODUCT_STORYBOARD_CONFIRMED: {
    template: "Storyboards.html",
    header: "YOUR STORYBOARDS ARE READY!",
    supplyText: "The storyboards from your latest job are ready:",
    emailBody: "",
    linkLabel: "PRODUCT STORYBOARDS",
    useSupplyPortal: true,
  },

  PRODUCT_STORYBOARD_NOT_CONFIRMED: {
    template: "Storyboards.html",
    header: "YOUR STORYBOARDS ARE READY!",
    supplyText: "The storyboards from your latest job are ready:",
    emailBody:
      "Please review the storyboards and confirm your selects so we can proceed with post-production.<br><br>Click the link below to see your storyboards and edit selects.",
    linkLabel: "Edit Selects",
    useSupplyPortal: true,
  },

  // Life job - no retouching, COD payment
  LIFE_NO_RETOUCHING_COD: {
    template: "Storyboards.html",
    header: "YOUR FILES ARE READY!",
    supplyText: "The storyboards from your latest job are ready:",
    emailBody:
      '<span style="color: rgb(220, 20, 60) !important;">For immediate file supply please email a bank remittance to <a href="mailto:production@imagesthatsell.com.au" style="color: inherit !important; text-decoration: underline !important;">production@imagesthatsell.com.au</a>.</span>',
    linkLabel: "VIEW STORYBOARDS",
    useSupplyPortal: true,
  },

  // Life job - no retouching, supply payment
  LIFE_NO_RETOUCHING_SUPPLY: {
    template: "Storyboards.html",
    header: "YOUR FILES ARE READY!",
    supplyText: "The files from your latest job are ready:",
    emailBody: "",
    linkLabel: "DOWNLOAD FILES",
    useSupplyPortal: true,
  },

  // Life job - retouching required, selects confirmed
  LIFE_RETOUCHING_CONFIRMED: {
    template: "Storyboards.html",
    header: "YOUR STORYBOARDS ARE READY!",
    supplyText: "The storyboards from your latest job are ready:",
    emailBody:
      "Management will reach out via email for confirmation regarding your selects.<br>Your job is on hold until we receive email confirmation of your selects.<br><br>Please note: this may affect your supply date.",
    linkLabel: "VIEW STORYBOARDS",
    useSupplyPortal: true,
  },

  // Life job - retouching required, selects not confirmed
  LIFE_RETOUCHING_NOT_CONFIRMED: {
    template: "Storyboards.html",
    header: "YOUR STORYBOARDS ARE READY!",
    supplyText: "The storyboards from your latest job are ready:",
    emailBody:
      "Please review the storyboards and confirm your selects so we can proceed with post-production.<br>Click the link below to see your storyboards or edit your selects.",
    linkLabel: "VIEW STORYBOARDS",
    useSupplyPortal: true,
  },

  // Internal only emails (product jobs)
  INTERNAL_ONLY: {
    template: "Storyboards.html",
    header: "",
    supplyText: "The storyboards for this job are ready:",
    emailBody: "",
    linkLabel: "PRODUCT STORYBOARDS",
    useSupplyPortal: true,
  },
};

// Helper function to load email templates
const loadTemplate = async (templateName) => {
  try {
    const response = await fetch(`/EmailTemplates/${templateName}`);
    if (!response.ok) {
      throw new Error(`Failed to load template: ${templateName}`);
    }
    return await response.text();
  } catch (error) {
    console.error("Error loading email template:", error);
    throw error;
  }
};

// Helper function to generate download link HTML
const generateDownloadLink = (url, label) => {
  return `<a href="${url}" contentEditable="false" target="_blank" style="display: inline-block; padding: 12px 24px; background-color: #000000; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: bold; margin: 8px;">${label}</a>`;
};


// Helper function to generate supply portal links
const generateLinks = (jobData, config) => {
  // Generate supply portal URL (handles all Dropbox access dynamically)
  const supplyUrl =
    process.env.NODE_ENV === "production"
      ? `https://supply.imagesthatsell.com.au/${jobData.id}`
      : `https://localhost:3000/supply/${jobData.id}`;

  return generateDownloadLink(supplyUrl, config.linkLabel);
};

// Main function to process template with variables
const processTemplate = (template, data) => {
  let processed = template;

  // Handle internal only emails
  if (data.isInternalOnly) {
    processed = processed.replace(/{{{header}}}/g, "");
    processed = processed.replace(/{{{supply_text}}}/g, data.supplyText || "");
  } else {
    processed = processed.replace(/{{{header}}}/g, data.header || "");
    processed = processed.replace(/{{{supply_text}}}/g, data.supplyText || "");
  }

  processed = processed.replace(/{{{subject}}}/g, data.subject || "");
  processed = processed.replace(/{{{links}}}/g, data.links || "");
  processed = processed.replace(/{{{email_body}}}/g, data.emailBody || "");

  return processed;
};

// Helper function to automatically determine email type based on job and user selections
export const getEmailType = (jobData, clientData, userSelections) => {
  const { selectsStatus, retouchingRequired, isInternalOnly } = userSelections;
  const isProductJob = jobData?.jobTypeFull?.includes("-P");
  const paymentTerms = clientData?.paymentTerms || "";

  if (isInternalOnly) {
    return "INTERNAL_ONLY";
  }

  if (isProductJob) {
    return selectsStatus === true
      ? "PRODUCT_STORYBOARD_CONFIRMED"
      : "PRODUCT_STORYBOARD_NOT_CONFIRMED";
  }

  if (!retouchingRequired) {
    return paymentTerms === "cod"
      ? "LIFE_NO_RETOUCHING_COD"
      : "LIFE_NO_RETOUCHING_SUPPLY";
  }

  return selectsStatus === true
    ? "LIFE_RETOUCHING_CONFIRMED"
    : "LIFE_RETOUCHING_NOT_CONFIRMED";
};

// Main unified function to generate email content
export const generateEmailContent = async (
  jobData,
  clientData,
  userSelections
) => {
  try {
    const emailType = getEmailType(jobData, clientData, userSelections);
    const config = EMAIL_CONFIGS[emailType];

    if (!config) {
      throw new Error(`Unknown email type: ${emailType}`);
    }

    // Load the email template
    const template = await loadTemplate(config.template);

    // Generate the download links
    const links = generateLinks(jobData, config);

    // Prepare email data
    const emailData = {
      subject: getJobName(jobData),
      header: config.header,
      supplyText: config.supplyText,
      emailBody: config.emailBody,
      links: links,
      isInternalOnly: userSelections.isInternalOnly || false,
    };

    // Process the template with the data
    const processedTemplate = processTemplate(template, emailData);

    return {
      template: processedTemplate,
      subject: emailData.subject,
      links: links,
      emailBody: emailData.emailBody,
      baseTemplate: template,
      emailData: emailData, // Return the data for dynamic updates
    };
  } catch (error) {
    console.error("Error generating email content:", error);
    throw error;
  }
};

// Function to update template dynamically (for subject changes)
export const updateTemplateWithSubject = (
  baseTemplate,
  emailData,
  newSubject
) => {
  const updatedData = { ...emailData, subject: newSubject };
  return processTemplate(baseTemplate, updatedData);
};

export { EMAIL_CONFIGS };

export default {
  generateEmailContent,
  getEmailType,
  updateTemplateWithSubject,
  EMAIL_CONFIGS,
};
